import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  buildFloatedToolbarViewportStyle,
  floatedToolbarTopStyle,
  resolveFloatedToolbarInContainer,
  resolveFloatedToolbarInHost,
} from '../../utils/slideToolbarPlacement';

export type ProgramSlideFloatedToolbarPlacement = {
  className: string;
  style: React.CSSProperties;
};

export type ProgramSlideFloatedToolbarProps = {
  /** 캔버스 inner DOM. 지정 시 portal로 렌더해 블럭 크기·overflow 영향을 받지 않음 */
  host?: HTMLElement | null;
  /** 지정 시 document.body fixed portal (평가지 모달 밖 표시) */
  coordinateRect?: DOMRect | null;
  /** viewport portal 시 툴바가 벗어나지 않도록 할 컨테이너(평가지 모달) */
  viewportContainerRect?: DOMRect | null;
  /** viewport portal 라이트 표면 (평가지 편집) */
  viewportSurface?: 'sheet-light';
  blockX: number;
  blockY: number;
  blockH: number;
  className: string;
  placement: ProgramSlideFloatedToolbarPlacement;
  zIndex: number;
  onPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  children: React.ReactNode;
};

const floatedSuffix = (className: string): string =>
  className.includes('image-toolbar')
    ? ' cp-program-slide-canvas-image-toolbar--floated'
    : ' cp-program-slide-canvas-toolbar--floated';

export function buildProgramSlideFloatedToolbarStyle(
  blockX: number,
  blockY: number,
  blockH: number,
  placement: ProgramSlideFloatedToolbarPlacement,
  zIndex: number
): React.CSSProperties {
  return {
    position: 'absolute',
    left: `${blockX * 100}%`,
    ...floatedToolbarTopStyle(blockY, blockH, placement.className),
    zIndex,
    pointerEvents: 'auto',
    ...placement.style,
  };
}

export const ProgramSlideFloatedToolbar: React.FC<ProgramSlideFloatedToolbarProps> = ({
  host,
  coordinateRect,
  viewportContainerRect,
  viewportSurface,
  blockX,
  blockY,
  blockH,
  className,
  placement,
  zIndex,
  onPointerDown,
  onMouseDown,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [viewportLayout, setViewportLayout] = useState<{
    top: number;
    left: number;
    placementClass: string;
  } | null>(null);
  const [hostLayout, setHostLayout] = useState<{
    top: number;
    left: number;
    placementClass: string;
  } | null>(null);

  const useViewportPortal = coordinateRect !== undefined;
  const useHostPortal = !useViewportPortal && !!host;

  useLayoutEffect(() => {
    if (!useViewportPortal || !coordinateRect) {
      setViewportLayout(null);
      return;
    }
    const el = panelRef.current;
    if (!el) return;

    const panelWidth = el.offsetWidth;
    const panelHeight = el.offsetHeight;
    if (panelWidth < 1 || panelHeight < 1) return;

    if (viewportContainerRect) {
      const resolved = resolveFloatedToolbarInContainer({
        containerRect: viewportContainerRect,
        canvasRect: coordinateRect,
        blockX,
        blockY,
        blockH,
        panelWidth,
        panelHeight,
        preferredPlacementClass: placement.className,
      });
      setViewportLayout(resolved);
      return;
    }

    const base = buildFloatedToolbarViewportStyle(
      coordinateRect,
      blockX,
      blockY,
      blockH,
      placement,
      zIndex
    );
    setViewportLayout({
      top: Math.round(parseFloat(String(base.top)) || 0),
      left: Math.round(parseFloat(String(base.left)) || 0),
      placementClass: placement.className,
    });
  }, [
    blockH,
    blockX,
    blockY,
    coordinateRect,
    placement,
    useViewportPortal,
    viewportContainerRect,
    zIndex,
  ]);

  useLayoutEffect(() => {
    if (!useHostPortal || !host) {
      setHostLayout(null);
      return;
    }
    const el = panelRef.current;
    if (!el) return;

    const measure = () => {
      const panelWidth = el.offsetWidth;
      const panelHeight = el.offsetHeight;
      if (panelWidth < 1 || panelHeight < 1) return;
      const resolved = resolveFloatedToolbarInHost({
        hostWidth: host.clientWidth,
        hostHeight: host.clientHeight,
        blockX,
        blockY,
        blockH,
        panelWidth,
        panelHeight,
        preferredPlacementClass: placement.className,
      });
      setHostLayout(resolved);
    };

    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    ro?.observe(host);
    return () => ro?.disconnect();
  }, [blockH, blockX, blockY, host, placement.className, useHostPortal]);

  if (useViewportPortal && !coordinateRect) return null;

  const resolvedPlacementClass = useViewportPortal
    ? viewportLayout?.placementClass ?? placement.className
    : useHostPortal
      ? hostLayout?.placementClass ?? placement.className
      : placement.className;

  const style = useViewportPortal
    ? viewportLayout
      ? {
          position: 'fixed' as const,
          left: `${viewportLayout.left}px`,
          top: `${viewportLayout.top}px`,
          zIndex,
          pointerEvents: 'auto' as const,
          ...placement.style,
        }
      : buildFloatedToolbarViewportStyle(coordinateRect!, blockX, blockY, blockH, placement, zIndex)
    : useHostPortal && hostLayout
      ? {
          position: 'absolute' as const,
          left: `${Math.round(hostLayout.left)}px`,
          top: `${Math.round(hostLayout.top)}px`,
          zIndex,
          pointerEvents: 'auto' as const,
          ...placement.style,
        }
      : buildProgramSlideFloatedToolbarStyle(blockX, blockY, blockH, placement, zIndex);

  const node = (
    <div
      ref={useViewportPortal || useHostPortal ? panelRef : undefined}
      className={
        className +
        floatedSuffix(className) +
        resolvedPlacementClass +
        (useViewportPortal ? ' cp-program-slide-canvas-toolbar--viewport-fixed' : '') +
        (useViewportPortal && viewportSurface === 'sheet-light'
          ? ' cp-program-slide-canvas-toolbar--sheet-light'
          : '')
      }
      style={style}
      onPointerDown={onPointerDown}
      onMouseDown={onMouseDown}
    >
      {children}
    </div>
  );

  if (useViewportPortal) return createPortal(node, document.body);
  if (!host) return node;
  return createPortal(node, host);
};
